import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { authHeaders } from '../listPageStyles';
import { Section, Grid, FieldSelect } from '../detail/DetailShell';

const API = '/api/representatives';

/**
 * #783 DİZAYNLAR SEKMESİ — temsilcinin belge başlığı → belge dizaynı (DocumentTemplate) atamaları.
 *
 * <b>Başlıklar sabittir</b> (talepteki 11 kalem) ve sırası korunur; değerler `DocumentDesignSlot`
 * enum'ıyla birebir aynıdır ve DB'de int olarak durur — araya yeni başlık eklerken mevcut
 * numaraları kaydırma, sona ekle.
 *
 * <b>Seçenekler distribütöre göre süzülür</b>: uç yalnız temsilcinin distribütörüne kayıtlı
 * şablonları + tüm distribütörler için geçerli GENEL şablonları döndürür (talep).
 *
 * <b>Belge TÜRÜ (DocType) filtresi UYGULANMAZ.</b> DocumentTemplate yalnız üç tür tanıyor
 * (0=Sipariş, 1=İrsaliye, 2=Fatura); buradaki 11 başlığın bir kısmının (Tahsilat, Proforma,
 * Taşıma İrsaliyesi…) o listede karşılığı YOK. Türe göre daraltmak o başlıkları seçilemez
 * kılardı — talep de zaten yalnız distribütör kısıtı istiyor.
 *
 * Kaydetme kartın TEK "Kaydet"ine bağlıdır (CompanyProfileTab ile aynı ref deseni): sekmeye ayrı
 * bir Kaydet koymak "hangi Kaydet neyi kaydediyor" sorusunu doğururdu.
 */
export const DESIGN_SLOTS = [
  { slot: 1, label: 'E-Arşiv Dizaynı' },
  { slot: 2, label: 'E-Fatura Dizaynı' },
  { slot: 3, label: 'E-İrsaliye Dizaynı' },
  { slot: 4, label: 'Düz İrsaliye Dizaynı' },
  { slot: 5, label: 'Taşıma İrsaliyesi Dizaynı' },
  { slot: 6, label: 'Sipariş Formu Dizaynı' },
  { slot: 7, label: 'Tahsilat Dizaynı' },
  { slot: 8, label: 'Proforma Fatura Dizaynı' },
  { slot: 9, label: 'Müşteriden İade Formu Dizaynı' },
  { slot: 10, label: 'Nama Sevk İrsaliye Dizaynı' },
  { slot: 11, label: 'Satıcıya İade Fatura Dizaynı' },
];

function unwrap(body) {
  if (!body) return null;
  return body.data ?? body.Data ?? body;
}

// Hata bildirimi parent'a bırakılır: `save()` fırlatır, kartın Kaydet akışı toast'ı gösterir
// (CompanyProfileTab ile aynı sözleşme) — sekmenin ayrı bir onToast'a ihtiyacı yok.
export const DesignsTab = forwardRef(function DesignsTab({ repId, readOnly = false }, ref) {
  // slot → templateId ('' = atanmamış)
  const [selection, setSelection] = useState({});
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!repId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/${repId}/document-designs`, { headers: authHeaders() });
      const d = unwrap(await res.json().catch(() => null));
      if (!res.ok || !d) { setLoading(false); return; }
      const map = {};
      for (const a of (d.assignments || [])) map[a.slot ?? a.Slot] = a.documentTemplateId ?? a.DocumentTemplateId;
      setSelection(map);
      setTemplates(d.templates || []);
    } catch {
      // Sessiz: sekme boş açılır, kullanıcı yeniden deneyebilir.
    } finally {
      setLoading(false);
    }
  }, [repId]);

  useEffect(() => { load(); }, [load]);

  /** Ana kart Kaydet'e bastığında çağrılır (parent → useImperativeHandle). */
  const save = useCallback(async () => {
    if (!repId || readOnly) return;
    const items = Object.entries(selection)
      .filter(([, templateId]) => !!templateId)
      .map(([slot, templateId]) => ({ slot: Number(slot), documentTemplateId: templateId }));

    const res = await fetch(`${API}/${repId}/document-designs`, {
      method: 'PUT',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.message || body?.Message || 'Dizayn atamaları kaydedilemedi');
    }
  }, [repId, readOnly, selection]);

  useImperativeHandle(ref, () => ({ save, isReadOnly: () => readOnly }), [save, readOnly]);

  const setSlot = (slot, templateId) => setSelection((c) => ({ ...c, [slot]: templateId }));

  // FieldSelect'in beklediği {value,label} listesi. Boş seçenek, atanmış bir başlığı tekrar
  // BOŞALTABİLMEK için gerekli (kaydetmede o başlık silinir).
  const options = [
    { value: '', label: 'Seçiniz' },
    ...templates.map((t) => ({ value: t.id || t.Id, label: t.name || t.Name })),
  ];

  return (
    <Section
      icon="layers"
      title="Belge Dizaynları"
      subtitle="Her belge başlığı için kullanılacak dizaynı seçin"
      note={
        templates.length === 0 && !loading
          ? 'Bu temsilcinin distribütörü için tanımlı belge dizaynı yok. Dizaynlar "Belge Dizaynı" ekranından tanımlanır.'
          : 'Listede yalnız bu temsilcinin distribütörüne tanımlı dizaynlar ve tüm distribütörler için geçerli genel dizaynlar görünür.'
      }
    >
      <Grid cols={2}>
        {DESIGN_SLOTS.map(({ slot, label }) => (
          <FieldSelect
            key={slot}
            form={selection}
            set={setSlot}
            k={slot}
            label={label}
            options={options}
            disabled={readOnly || loading || templates.length === 0}
          />
        ))}
      </Grid>
    </Section>
  );
});

export default DesignsTab;
